import React, {Component, Fragment} from 'react';
import '../../style/logPagination.css';

const LEFT_PAGE = 'LEFT';
const RIGHT_PAGE = 'RIGHT';

const range = (from, to, step = 1) => {
    const out = [];
    for (let i = from; i <= to; i += step) {
        out.push(i);
    }
    return out;
};

/** Slice items for client-side pagination (1-based page). */
export function paginateItems(items, page = 1, pageSize = 20) {
    const list = Array.isArray(items) ? items : [];
    const total = list.length;
    const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);
    const safePage = Math.min(Math.max(1, Number(page) || 1), totalPages);
    const start = (safePage - 1) * pageSize;
    return {
        page: safePage,
        pageSize,
        total,
        totalPages,
        pageItems: list.slice(start, start + pageSize),
    };
}

/**
 * Client-side list pagination controls.
 * props: totalPages, page (optional controlled), onNewPage(page1Based)
 */
class ListPagination extends Component {
    state = {
        currentPage: this.props.page || 1,
    };

    componentDidUpdate(prevProps) {
        if (this.props.page != null && this.props.page !== prevProps.page) {
            this.setState({currentPage: this.props.page});
        }
        if (this.props.totalPages !== prevProps.totalPages) {
            const max = Math.max(1, this.props.totalPages || 1);
            if (this.state.currentPage > max) {
                this.gotoPage(max);
            }
        }
    }

    gotoPage = (page) => {
        const totalPages = Math.max(1, this.props.totalPages || 1);
        const next = Math.min(Math.max(1, page), totalPages);
        this.setState({currentPage: next});
        if (typeof this.props.onNewPage === 'function') {
            this.props.onNewPage(next);
        }
    };

    handleClick = (page) => (evt) => {
        evt.preventDefault();
        this.gotoPage(page);
    };

    handleMoveLeft = (evt) => {
        evt.preventDefault();
        this.gotoPage(this.state.currentPage - 1);
    };

    handleMoveRight = (evt) => {
        evt.preventDefault();
        this.gotoPage(this.state.currentPage + 1);
    };

    fetchPageNumbers = () => {
        const totalPages = Math.max(1, this.props.totalPages || 1);
        const currentPage = this.state.currentPage;
        const pageNeighbours = 2;
        const totalNumbers = 7;
        const totalBlocks = totalNumbers + 2;

        if (totalPages > totalBlocks) {
            const startPage = Math.max(2, currentPage - pageNeighbours);
            const endPage = Math.min(totalPages - 1, currentPage + pageNeighbours);
            let pages = range(startPage, endPage);
            const hasLeftSpill = startPage > 2;
            const hasRightSpill = (totalPages - endPage) > 1;
            const spillOffset = totalNumbers - (pages.length + 1);

            if (hasLeftSpill && !hasRightSpill) {
                pages = [LEFT_PAGE, ...range(startPage - spillOffset, startPage - 1), ...pages];
            } else if (!hasLeftSpill && hasRightSpill) {
                pages = [...pages, ...range(endPage + 1, endPage + spillOffset), RIGHT_PAGE];
            } else if (hasLeftSpill && hasRightSpill) {
                pages = [LEFT_PAGE, ...pages, RIGHT_PAGE];
            }
            return [1, ...pages, totalPages];
        }
        return range(1, totalPages);
    };

    render() {
        const totalPages = Math.max(1, this.props.totalPages || 1);
        if (totalPages <= 1) {
            return null;
        }
        const {currentPage} = this.state;
        const pages = this.fetchPageNumbers();

        return (
            <Fragment>
                <nav aria-label="Pagination" className="d-flex justify-content-center my-3">
                    <ul className="pagination">
                        {pages.map((page, index) => {
                            if (page === LEFT_PAGE) {
                                return (
                                    <li key={index} className="page-item">
                                        <a className="page-link" href="#prev" aria-label="Previous"
                                           onClick={this.handleMoveLeft}>
                                            <span aria-hidden="true">&laquo;</span>
                                        </a>
                                    </li>
                                );
                            }
                            if (page === RIGHT_PAGE) {
                                return (
                                    <li key={index} className="page-item">
                                        <a className="page-link" href="#next" aria-label="Next"
                                           onClick={this.handleMoveRight}>
                                            <span aria-hidden="true">&raquo;</span>
                                        </a>
                                    </li>
                                );
                            }
                            return (
                                <li key={index} className={`page-item${currentPage === page ? ' active' : ''}`}>
                                    <a className="page-link" href={`#page-${page}`}
                                       onClick={this.handleClick(page)}>{page}</a>
                                </li>
                            );
                        })}
                    </ul>
                </nav>
            </Fragment>
        );
    }
}

export default ListPagination;
